import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { Toaster } from "sonner";
import { useEffect, useRef, useState } from "react";

import logo from "../assets/logo.png";
import { tx } from "@/i18n";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <img src={logo} alt="" className="mx-auto mb-4 h-20 w-20 object-contain" />
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          {tx("Page not found", undefined, "Pagina nu a fost găsită")}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {tx(
            "The page you're looking for doesn't exist or has been moved.",
            undefined,
            "Pagina pe care o cauți nu există sau a fost mutată."
          )}
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {tx("Go home", undefined, "Acasă")}
          </Link>
        </div>
      </div>
    </div>
  );
}

// Module-level guard: only auto-retry once per browser tab session, so a
// genuinely broken page can't trap users in an infinite reload loop.
let autoRetried = false;

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const [retrying, setRetrying] = useState(!autoRetried);
  const didRunRef = useRef(false);

  // Transient first-render failures (auth session not yet hydrated, a slow
  // initial query, a stale lazy chunk after a deploy) reliably succeed on the
  // second attempt. Auto-retry once before showing the user-facing error UI.
  useEffect(() => {
    if (didRunRef.current) return;
    didRunRef.current = true;
    if (autoRetried) return;
    autoRetried = true;
    const t = window.setTimeout(() => {
      try {
        router.invalidate();
        reset();
      } finally {
        setRetrying(false);
      }
    }, 150);
    return () => window.clearTimeout(t);
  }, [router, reset]);

  if (retrying) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <img src={logo} alt="" className="h-32 w-32 animate-pulse object-contain" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {tx("This page didn't load", undefined, "Pagina nu s-a încărcat")}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {tx(
            "Something went wrong on our end. You can try refreshing or head back home.",
            undefined,
            "Ceva nu a mers bine. Încearcă să reîncarci sau întoarce-te acasă."
          )}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {tx("Try again", undefined, "Încearcă din nou")}
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {tx("Go home", undefined, "Acasă")}
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster
        position="top-center"
        richColors
        offset={16}
      />
    </QueryClientProvider>
  );
}
